import React, { useEffect, useState } from "react";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import * as api from "../../api/index";
import { jwtDecode } from "jwt-decode";
import { FaMapMarkerAlt, FaStore, FaBoxOpen } from "react-icons/fa";
import { FiHome, FiPlusSquare, FiMail, FiUser } from "react-icons/fi";

export default function BookmarkPage() {
  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  const userId = decoded?.id || decoded?._id || null;
  const typeofuser = decoded?.typeofuser;

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("users");

  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [userPage, setUserPage] = useState(1);
  const [productPage, setProductPage] = useState(1);
  const limit = 20;

  const fetchBookmarks = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError("");

      const [usersRes, productsRes] = await Promise.all([
        api.getBookmarkedUsers({ userId, page: userPage, limit }),
        api.getBookmarkedProducts({ userId, page: productPage, limit }),
      ]);

      if (usersRes?.data?.success) setUsers(usersRes.data.users);
      if (productsRes?.data?.success) setProducts(productsRes.data.products);
    } catch (err) {
      console.error("Error fetching bookmarks:", err);
      setError("Failed to load bookmarks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookmarks();
  }, [userPage, productPage]);

  useEffect(() => {
    if (!token) navigate("/auth");
  }, [token]);

  const handleNext = () => {
    if (activeTab === "users") setUserPage((p) => p + 1);
    else setProductPage((p) => p + 1);
  };

  const handlePrev = () => {
    if (activeTab === "users" && userPage > 1) setUserPage((p) => p - 1);
    else if (activeTab === "products" && productPage > 1)
      setProductPage((p) => p - 1);
  };

  const currentData = activeTab === "users" ? users : products;
  const currentPage = activeTab === "users" ? userPage : productPage;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 pb-24">
      {/* 🔝 Sticky Navbar */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex justify-between items-center shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition flex items-center gap-1 text-gray-700"
        >
          <IoMdArrowBack size={24} />
          <span className="hidden sm:inline font-bold">Back</span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Saved Items</h1>
        <div className="w-10"></div> {/* Spacer for alignment */}
      </div>

      <div className="max-w-6xl mx-auto w-full p-4 sm:p-6">
        {/* 🧭 Modern Tab Switcher */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-200/50 p-1.5 rounded-[2rem] flex items-center gap-1 shadow-inner">
            <button
              onClick={() => setActiveTab("users")}
              className={`flex items-center gap-2 px-8 py-3 rounded-[1.5rem] font-bold text-sm transition-all duration-300 ${
                activeTab === "users"
                  ? "bg-white text-orange-600 shadow-sm scale-105"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <FaStore /> Sellers
            </button>
            <button
              onClick={() => setActiveTab("products")}
              className={`flex items-center gap-2 px-8 py-3 rounded-[1.5rem] font-bold text-sm transition-all duration-300 ${
                activeTab === "products"
                  ? "bg-white text-orange-600 shadow-sm scale-105"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <FaBoxOpen /> Products
            </button>
          </div>
        </div>

        {/* 🕒 Loading State */}
        {loading && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="h-64 bg-gray-200 animate-pulse rounded-[2rem]" />
            ))}
          </div>
        )}

        {/* 🧾 Data Grid */}
        {!loading && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {currentData.length > 0 ? (
                currentData.map((item) => (
                  <div
                    key={item._id}
                    className="group bg-white rounded-[2rem] border border-gray-100 flex flex-col overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                  >
                    <div className="relative h-32 sm:h-48 overflow-hidden">
                      <img
                        src={(activeTab === "users" ? item.profileImage : item.images?.[0]) || "/placeholder.jpg"}
                        alt={activeTab === "users" ? item.Name : item.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                      />
                      {activeTab === "users" && (
                        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur px-2 py-1 rounded-lg text-[10px] font-black flex items-center gap-1">
                          <FaMapMarkerAlt className="text-orange-600" />
                          {item.lga || "Lagos"}
                        </div>
                      )}
                    </div>

                    <div className="p-3 sm:p-5 flex flex-col flex-1">
                      <h2 className="font-black text-gray-900 text-sm sm:text-lg truncate group-hover:text-orange-600 transition">
                        {activeTab === "users" ? item.Name : item.name}
                      </h2>

                      {activeTab === "products" ? (
                        <>
                          <p className="text-orange-600 font-black text-sm sm:text-lg mt-1">
                            ₦{item.price?.toLocaleString() || 0}
                          </p>
                          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-tight mt-1 truncate">
                            {item.product} • {item.model}
                          </p>
                        </>
                      ) : (
                        <p className="text-gray-500 text-[11px] sm:text-sm mt-1 line-clamp-2 min-h-[32px]">
                          {item.bio || item.address || "Verified Seller"}
                        </p>
                      )}

                      <div className="mt-4 pt-4 border-t border-gray-50">
                        <button
                          onClick={() => navigate(activeTab === "users" ? `/shop/${item._id}` : `/view-product/${item._id}`)}
                          className="w-full bg-gray-900 text-white py-2.5 rounded-2xl hover:bg-orange-600 transition shadow-lg text-[11px] sm:text-sm font-bold"
                        >
                          {activeTab === "users" ? "View Store" : "View Product"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-20 bg-white rounded-[3rem] border-2 border-dashed border-gray-100">
                  <div className="text-5xl mb-4">⭐</div>
                  <h3 className="text-xl font-bold text-gray-900">Nothing saved yet</h3>
                  <p className="text-gray-500 max-w-xs mx-auto mt-2">
                    Bookmark {activeTab} you're interested in to find them here later.
                  </p>
                </div>
              )}
            </div>

            {/* ⏩ Pagination */}
            {currentData.length > 0 && (
              <div className="flex justify-center items-center gap-6 mt-12 mb-10">
                <button
                  onClick={handlePrev}
                  disabled={currentPage === 1}
                  className={`px-6 py-2 rounded-xl font-bold transition-all ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                      : "bg-white text-gray-700 shadow-md hover:bg-gray-50"
                  }`}
                >
                  Prev
                </button>
                <span className="text-gray-900 font-black text-sm uppercase tracking-widest">
                  Page {currentPage}
                </span>
                <button
                  onClick={handleNext}
                  className="px-6 py-2 rounded-xl font-bold bg-white text-gray-700 shadow-md hover:bg-gray-50 transition-all"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 🧭 Bottom Navigation Bar */}
    
    </div>
  );
}